import { FC } from "preact/compat";
import { Link, useNavigate } from "react-router-dom";
import { Project, useApp } from "~/core";
import { me } from "~/core/store";
import { Approvers } from "~/organisms";
import { CreateFromBudget } from "~/routes/project/shared/CreateFromBudget";
import { isConversionDone } from "~/routes/project/shared/CreateFromBudget/utils";
import { getCodeString } from "~/utils";
import useWidget from "../../../Desktop/widgets/PendingBudgets/useWidget";
import { SingleWidgetProps } from "../interfaces";
import ChevronRightIcon from "@material-design-icons/svg/filled/chevron_right.svg";
import CheckCircleIcon from "@material-design-icons/svg/outlined/check_circle.svg";
import CircleIcon from "@material-design-icons/svg/outlined/circle.svg";
import DoneAllIcon from "@material-design-icons/svg/outlined/done_all.svg";
import HourglassIcon from "@material-design-icons/svg/outlined/hourglass_empty.svg";
import BudgetIcon from "@material-design-icons/svg/outlined/savings.svg";

const MAX_SHOW_COUNT = 4;

const STUB_FN = () => {};

const Widget: FC<SingleWidgetProps> = ({ className }) => {
    const { api } = useApp();
    const navigate = useNavigate();
    const { budgets, loading } = useWidget({
        id: "pending-budgets-mobile",
        onLink: STUB_FN,
    });
    const show = budgets.value.slice(0, MAX_SHOW_COUNT);

    const handleOpenBudget = (project: Project | null, budgetId: string) => {
        if (!project) {
            return;
        }

        navigate(me(`projects/${project._id.toString()}/general`));
        navigate(me(`projects/${project._id.toString()}/budget?id=${budgetId}`));
    };

    return (
        <div className={className}>
            <div className="flex justify-between border-b border-gray-200 py-1">
                <div className="flex items-center gap-x-2 text-gray-500 font-bold px-2 py-1">
                    <BudgetIcon
                        width="20"
                        height="20"
                        className="fill-current"
                    />
                    <span>Pending Budgets</span>
                </div>
                <Link
                    to={me("projects")}
                    className="flex items-center gap-x-1 text-gray-500 font-bold px-2 py-1"
                >
                    <span>View all</span>
                    <ChevronRightIcon
                        width="20"
                        height="20"
                        className="fill-current"
                    />
                </Link>
            </div>
            <div className="p-4 flex flex-col gap-y-2">
                {!loading.value && show.length === 0 && (
                    <div className="text-center">No pending budgets.</div>
                )}
                {show.map(({ budget, project, approval }) => {
                    const sentInternal = approval.internal.sent;
                    const approvedInternal = approval.internal.approved;
                    const sentClient = approval.external.sent;
                    const approvedClient = approval.external.approved;
                    const title = approvedClient
                        ? "Approved by client"
                        : sentClient
                          ? "Sent to client"
                          : approvedInternal
                            ? "Approved internally"
                            : sentInternal
                              ? "Sent internally"
                              : "Not sent";
                    const conversionDone = isConversionDone(api, budget);

                    return (
                        <div
                            key={budget.id}
                            className="flex flex-col gap-y-2 rounded-2xl border border-gray-200 px-3 py-2"
                        >
                            <div className="flex items-center gap-x-2">
                                <button
                                    type="button"
                                    onClick={() =>
                                        handleOpenBudget(project, budget.id)
                                    }
                                    className="block min-w-0 flex-1 text-left"
                                >
                                    <div className="flex min-w-0 items-center gap-x-2">
                                        <span
                                            className="flex-shrink-0"
                                            title={title}
                                        >
                                            {approvedClient ? (
                                                <CheckCircleIcon
                                                    width="18"
                                                    height="18"
                                                    className="fill-lime-600"
                                                />
                                            ) : sentClient ? (
                                                <HourglassIcon
                                                    width="18"
                                                    height="18"
                                                    className="fill-gray-400"
                                                />
                                            ) : approvedInternal ? (
                                                <div className="relative">
                                                    <CheckCircleIcon
                                                        width="18"
                                                        height="18"
                                                        className="fill-lime-600"
                                                    />
                                                    <div className="absolute -right-1 -top-1 size-2 rounded-full border border-white bg-gray-400" />
                                                </div>
                                            ) : sentInternal ? (
                                                <HourglassIcon
                                                    width="18"
                                                    height="18"
                                                    className="fill-gray-400"
                                                />
                                            ) : (
                                                <CircleIcon
                                                    width="18"
                                                    height="18"
                                                    className="fill-gray-400"
                                                />
                                            )}
                                        </span>
                                        <p className="truncate font-bold">
                                            {budget.header}
                                        </p>
                                    </div>
                                    <div className="mt-1 truncate text-xs text-gray-500">
                                        {project ? (
                                            <>
                                                {project.client}{" "}
                                                {getCodeString(project.project)}{" "}
                                                - {project.name}
                                            </>
                                        ) : (
                                            "-"
                                        )}
                                    </div>
                                </button>
                                {(sentInternal || sentClient) && (
                                    <Approvers
                                        approvals={
                                            sentClient
                                                ? approval.external.approvals
                                                : approval.internal.approvals
                                        }
                                        mobile
                                        emptyLabel=""
                                    />
                                )}
                                {approvedClient && !conversionDone && (
                                    <CreateFromBudget
                                        budget={budget}
                                        renderTrigger={(open) => (
                                            <button
                                                type="button"
                                                title="Create from budget"
                                                onClick={open}
                                                className="flex-shrink-0 p-1"
                                            >
                                                <DoneAllIcon
                                                    width="20"
                                                    height="20"
                                                    className="fill-green-500"
                                                />
                                            </button>
                                        )}
                                    />
                                )}
                            </div>
                            <Link
                                to={me(
                                    `projects/${project?._id.toString()}/general`,
                                )}
                            >
                                open project
                            </Link>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default Widget;
